import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { TextField, Box, Typography } from '@mui/material';
import ActionDialog from 'common/components/ActionDialog';
import { InventoryFields } from 'common/modules/inventory';
import { useTranslation } from 'services/localization/useTranslation';
import { api } from 'common/frontend/api';
import * as ShopSelectors from 'selectors/ShopSelectors';

interface Props {
	isOpen: boolean;
	columnId: string;
	inventoryFields: InventoryFields;
	onClose: () => void;
}

const RenameColumnDialog = (props: Props) => {
	const { t } = useTranslation();
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(false);
	const columnLabel = props.inventoryFields.values[props.columnId]?.label ?? 'Unknown';
	const [editedColumnLabel, setEditedColumnLabel] = useState(columnLabel);
	const shopId = useSelector(ShopSelectors.activeShopId)!;

	const handleSave = async () => {
		if (!props.inventoryFields.values[props.columnId]) return;
		setSaving(true);
		try {
			await api()
				.inventoryConfigurations.doc(shopId)
				.update({
					[`fields.values.${props.columnId}.label` as `fields.values.${123}.label`]: editedColumnLabel,
				});
			setSaving(false);
			props.onClose();
		} catch (e) {
			setSaving(false);
			setError(true);
		}
	};

	const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setError(false);
		setEditedColumnLabel(e.target.value);
	};

	return props.isOpen ? (
		<ActionDialog
			title={t('inventory.articles.table.columnsRenameTitle', 'Rename column')}
			headerVariant="contained"
			btnActions={{
				primary: {
					text: t('common:actions.save'),
					loading: saving,
					disabled: saving || !editedColumnLabel.length,
					onClick: handleSave,
				},
				secondary: {
					text: t('common:actions.cancel'),
					disabled: saving,
					onClick: props.onClose,
				},
			}}
			onClose={props.onClose}
		>
			<Box mt={1}>
				<TextField
					label={t('inventory.articles.table.columnsRenameLabel', 'Column name')}
					variant="outlined"
					value={editedColumnLabel}
					onChange={handleNameChange}
					disabled={saving}
				/>
				{error && (
					<Box mt={1}>
						<Typography variant="body2" color="error">
							{t('common:errors.errorMessage')}
						</Typography>
					</Box>
				)}
			</Box>
		</ActionDialog>
	) : null;
};

export default RenameColumnDialog;
